import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for detecting swipe gestures on mobile devices
 * Handles left/right swipes for tab navigation
 */
export const useSwipeNavigation = (onSwipeLeft, onSwipeRight, enabled = true) => {
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndX = useRef(null);
  const touchEndY = useRef(null);
  const [isSwiping, setIsSwiping] = useState(false);

  // Minimum swipe distance to trigger navigation (in pixels)
  const minSwipeDistance = 50;
  // Maximum vertical movement allowed for horizontal swipe
  const maxVerticalMovement = 100;

  const handleTouchStart = (e) => {
    if (!enabled) return;
    
    // Only handle single finger touches
    if (e.touches.length !== 1) return;
    
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchEndX.current = null;
    touchEndY.current = null;
    setIsSwiping(false);
  };

  const handleTouchMove = (e) => {
    if (!enabled) return;
    
    if (e.touches.length !== 1 || touchStartX.current === null) return;
    
    const touch = e.touches[0];
    touchEndX.current = touch.clientX;
    touchEndY.current = touch.clientY;
    
    // Calculate movement
    const deltaX = Math.abs(touchEndX.current - touchStartX.current);
    const deltaY = Math.abs(touchEndY.current - touchStartY.current);
    
    // If horizontal movement is significant and vertical movement is small,
    // we're likely doing a horizontal swipe
    if (deltaX > 20 && deltaY < maxVerticalMovement && deltaX > deltaY) {
      setIsSwiping(true);
      // Prevent default scrolling behavior during horizontal swipe
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e) => {
    if (!enabled) return;
    
    if (touchStartX.current === null || touchEndX.current === null) {
      reset();
      return;
    }

    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = Math.abs((touchEndY.current || touchStartY.current) - touchStartY.current);
    
    // Check if this was a valid horizontal swipe
    const isHorizontalSwipe = Math.abs(deltaX) > minSwipeDistance && deltaY < maxVerticalMovement;
    
    if (isHorizontalSwipe) {
      if (deltaX > 0) {
        // Swiped right (previous tab)
        onSwipeRight && onSwipeRight();
      } else {
        // Swiped left (next tab)
        onSwipeLeft && onSwipeLeft();
      }
    }
    
    reset();
  };

  const reset = () => {
    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
    touchEndY.current = null;
    setIsSwiping(false);
  };

  // Detect if device is mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
  };

  const swipeHandlers = isMobile() ? {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  } : {};

  return {
    swipeHandlers,
    isSwiping,
    isMobile: isMobile()
  };
};